import React, { useState, useEffect } from "react";
import { authWithPiNetwork, CreatePayment } from "../utils/PiIntegration";
import { useNavigate } from "react-router-dom";

interface DonationModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectName?: string;
}

const DonationModal: React.FC<DonationModalProps> = ({
  isOpen,
  onClose,
  projectName = "Arcadia",
}) => {
  const [amount, setAmount] = useState<string | number>("");
  const [customAmount, setCustomAmount] = useState("");
  const [isDonated, setIsDonated] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Preset donation amounts
  const presetAmounts = [5, 10, 25, 50];

  useEffect(() => {
    // Add class to body to prevent scrolling when modal is open
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    // Reset state when modal is opened
    if (isOpen) {
      setAmount("");
      setCustomAmount("");
      setIsDonated(false);
    }

    // Cleanup function
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Handle preset amount selection
  const handleAmountClick = (value: any) => {
    setAmount(value);
    setCustomAmount("");
  };

  // Handle custom amount input
  const handleCustomAmountChange = (e: any) => {
    const value = e.target.value;
    // Only allow numbers and decimal point
    if (/^\d*\.?\d{0,2}$/.test(value) || value === "") {
      setCustomAmount(value);
      setAmount("custom");
    }
  };
  const navigate = useNavigate();

  // Handle donation submission
  const handleDonate = async () => {
    const donationAmount =
      amount === "custom" ? parseFloat(customAmount) : Number(amount);

    if (!donationAmount || isNaN(donationAmount) || donationAmount <= 0) {
      alert("Please enter a valid donation amount.");
      return;
    }

    setIsProcessing(true);
    if (
      !window.location.href?.includes("arcadia-play.web.app") &&
      !window.location.href?.includes("arcadia7666.pinet.com")
    ) {
      window.open(`https://arcadia-play.web.app/donate/${donationAmount}`);
      return;
    }
    const userPiData = await authWithPiNetwork();
    await CreatePayment(
      userPiData?.data?.userUid,
      donationAmount,
      "donation",
      () => {
        setIsProcessing(false);
        setIsDonated(true);
      }
    );
  };

  // Close modal and reset state
  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setIsDonated(false);
    }, 300);
  };

  // Don't render if modal is not open
  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
        backdropFilter: "blur(5px)",
        animation: "fadeIn 0.3s ease",
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "16px",
          width: "90%",
          maxWidth: "500px",
          maxHeight: "90vh",
          overflow: "auto",
          boxShadow: "0 20px 50px rgba(0, 0, 0, 0.3)",
          position: "relative",
          animation: "slideUp 0.4s ease",
        }}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          style={{
            position: "absolute",
            top: "15px",
            right: "15px",
            background: "transparent",
            border: "none",
            fontSize: "22px",
            cursor: "pointer",
            color: "#302b63",
            width: "36px",
            height: "36px",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "background-color 0.2s",
          }}
        >
          ✕
        </button>

        {/* Modal header */}
        <div
          style={{
            background:
              "linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)",
            color: "white",
            padding: "30px",
            borderTopLeftRadius: "16px",
            borderTopRightRadius: "16px",
            textAlign: "center",
          }}
        >
          {!isDonated ? (
            <>
              <h2
                style={{
                  margin: 0,
                  fontSize: "28px",
                  fontWeight: "bold",
                  marginBottom: "10px",
                }}
              >
                Support {projectName}
              </h2>
              <p
                style={{
                  margin: 0,
                  fontSize: "16px",
                  opacity: 0.9,
                }}
              >
                Your donation helps us bring serverless gaming to more players
              </p>
            </>
          ) : (
            <h2
              style={{
                margin: 0,
                fontSize: "28px",
                fontWeight: "bold",
              }}
            >
              Thank You!
            </h2>
          )}
        </div>

        {/* Modal content */}
        <div
          style={{
            padding: "30px",
          }}
        >
          {!isDonated ? (
            <>
              <p
                style={{
                  textAlign: "center",
                  marginBottom: "25px",
                  fontSize: "16px",
                  color: "#555",
                }}
              >
                Choose an amount to donate or enter a custom amount
              </p>

              {/* Preset amounts */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: "15px",
                  marginBottom: "20px",
                }}
              >
                {presetAmounts.map((presetAmount) => (
                  <button
                    key={presetAmount}
                    onClick={() => handleAmountClick(presetAmount)}
                    style={{
                      padding: "15px",
                      border:
                        amount === presetAmount
                          ? "2px solid #61dafb"
                          : "1px solid #e0e0e0",
                      borderRadius: "8px",
                      backgroundColor:
                        amount === presetAmount
                          ? "rgba(97, 218, 251, 0.1)"
                          : "white",
                      cursor: "pointer",
                      fontSize: "18px",
                      fontWeight: amount === presetAmount ? "bold" : "normal",
                      transition: "all 0.2s ease",
                      color: amount === presetAmount ? "#302b63" : "#555",
                    }}
                  >
                    {presetAmount}Pi
                  </button>
                ))}
              </div>

              {/* Custom amount */}
              <div
                style={{
                  marginBottom: "30px",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    position: "relative",
                  }}
                >
                  <input
                    type="text"
                    placeholder="Enter custom amount"
                    value={customAmount}
                    onChange={handleCustomAmountChange}
                    onClick={() => setAmount("custom")}
                    style={{
                      width: "100%",
                      padding: "15px",
                      paddingLeft: "30px",
                      borderRadius: "8px",
                      border:
                        amount === "custom"
                          ? "2px solid #61dafb"
                          : "1px solid #e0e0e0",
                      fontSize: "16px",
                      boxSizing: "border-box",
                      backgroundColor:
                        amount === "custom"
                          ? "rgba(97, 218, 251, 0.1)"
                          : "white",
                      outline: "none",
                    }}
                  />
                </div>
              </div>

              {/* Donate button */}
              <button
                onClick={handleDonate}
                disabled={
                  isProcessing ||
                  !amount ||
                  (amount === "custom" && !customAmount)
                }
                style={{
                  width: "100%",
                  padding: "16px",
                  backgroundColor: "#61dafb",
                  color: "#1a1a2e",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "18px",
                  fontWeight: "bold",
                  cursor:
                    !amount || (amount === "custom" && !customAmount)
                      ? "not-allowed"
                      : "pointer",
                  opacity:
                    !amount || (amount === "custom" && !customAmount) ? 0.7 : 1,
                  transition: "transform 0.2s, opacity 0.2s",
                  boxShadow: "0 4px 10px rgba(97, 218, 251, 0.25)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                {isProcessing ? (
                  <>
                    <span
                      style={{
                        display: "inline-block",
                        width: "20px",
                        height: "20px",
                        borderRadius: "50%",
                        border: "3px solid rgba(255, 255, 255, 0.3)",
                        borderTopColor: "#1a1a2e",
                        animation: "spin 1s linear infinite",
                      }}
                    ></span>
                    Processing...
                  </>
                ) : (
                  `Donate ${
                    amount
                      ? amount === "custom"
                        ? `${customAmount}Pi`
                        : `${amount}Pi`
                      : ""
                  }`
                )}
              </button>
            </>
          ) : (
            <div
              style={{
                textAlign: "center",
                animation: "fadeIn 0.5s ease",
              }}
            >
              <div
                style={{
                  width: "80px",
                  height: "80px",
                  borderRadius: "50%",
                  backgroundColor: "rgba(97, 218, 251, 0.2)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  margin: "0 auto 20px auto",
                }}
              >
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#61dafb"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
              </div>
              <h3
                style={{
                  color: "#1a1a2e",
                  fontSize: "22px",
                  marginBottom: "15px",
                }}
              >
                Your donation was successful!
              </h3>
              <p
                style={{
                  color: "#555",
                  fontSize: "16px",
                  lineHeight: 1.6,
                  marginBottom: "25px",
                }}
              >
                Thank you for supporting {projectName}. Your contribution helps
                us continue developing serverless gaming experiences for
                everyone.
              </p>
              <button
                onClick={handleClose}
                style={{
                  padding: "12px 24px",
                  backgroundColor: "#61dafb",
                  color: "#1a1a2e",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "16px",
                  fontWeight: "bold",
                  cursor: "pointer",
                  boxShadow: "0 4px 10px rgba(97, 218, 251, 0.25)",
                  transition: "transform 0.2s",
                }}
              >
                Continue Exploring
              </button>
            </div>
          )}
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideUp {
          from { transform: translateY(30px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

const DonateButton = ({ label = "Donate", projectName = "Arcadia" }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        marginBottom: "10px",
      }}
    >
      <button
        onClick={() => setIsModalOpen(true)}
        style={{
          background: "#61dafb",
          color: "#1a1a2e",
          border: "none",
          padding: "12px 24px",
          fontSize: "16px",
          fontWeight: "bold",
          borderRadius: "8px",
          cursor: "pointer",
          transition: "transform 0.2s, box-shadow 0.2s",
          boxShadow: "0 4px 12px rgba(97, 218, 251, 0.25)",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
        </svg>
        {label}
      </button>
      {/*The modal is conditionally rendered, and its position is managed by the styles within the DonationModal component itself. */}
      <DonationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        projectName={projectName}
      />
    </div>
  );
};

export default DonateButton;
